import classNames from 'classnames';
import { Link } from 'react-router';

import { useStandardApiRequest } from 'my-core/hooks';
import { findChild, renderContentMap } from 'my-core/online-course-content-map';
import { studentCoursePageAssessmentRelativePath } from 'my-core/routes';

import InCourseExperienceHeader from 'my-components/OnlineCourse/InCourseExperience/InCourseExperienceHeader';

import {
  StudentCoursePageBookletCard,
  StudentCoursePageCard,
  StudentCoursePageContentsWrapper,
  StudentCoursePageFlashCardsDeckCard,
  StudentCoursePageSectionHeader,
  StudentCoursePageTwoColumnLayout,
} from '../StudentCoursePageCommon';
import StudentCoursePageGradeBoostersList from '../StudentCoursePageGradeBoostersList';

import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck as faCircleCheckSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { updateUserCourse } from 'my-actions/UserCourseActions';

export default function StudentCoursePageAssesmentPage({
  assessment,
  classPassId,
  course,
  courseSubscription,
  currentUser,
  drawerOpen,
  fetching,
  flashCardDecks,
  onlineCourses,
  onToggleDrawer,
  studySessionBundles,
  studySessions,
  subscription,
  userCourse,
}) {
  const completedChapterIds = userCourse?.assessments_progress?.[assessment?.id] || [];

  const { performRequest: performUpdateUserCourse } = useStandardApiRequest({
    actionCreator: updateUserCourse,
    errorMessage: true,
  });

  function updateCompletedChapters(chapterIds) {
    if (userCourse)
      performUpdateUserCourse(userCourse.id, {
        assessments_progress: { ...userCourse.assessments_progress, [assessment.id]: chapterIds },
      });
  }

  const assessmentStudySessions = studySessions.filter(ss => !ss.assessment_id || ss.assessment_id === assessment?.id);
  const assessmentOnlineCourses = onlineCourses.filter(
    oc => !oc.course_assessment_id || oc.course_assessment_id === assessment?.id,
  );
  const assessmentStudySessionBundles = studySessionBundles.filter(b =>
    b.study_sessions.some(ss => assessmentStudySessions.some(as => as.id === ss.id)),
  );
  const hasGradeBoosters = !!(assessmentStudySessions.length || assessmentOnlineCourses.length);

  const basePath = `../${studentCoursePageAssessmentRelativePath(assessment)}`;
  return (
    <>
      <InCourseExperienceHeader
        drawerOpen={drawerOpen}
        onToggleDrawer={onToggleDrawer}
        title={`${assessment?.name || ''} Overview`}
        titleVariant="h6"
      />

      <StudentCoursePageContentsWrapper>
        <StudentCoursePageTwoColumnLayout>
          <div>
            {hasGradeBoosters ?
              !fetching && (
                <StudentCoursePageGradeBoostersList
                  assessmentName={assessment?.name}
                  course={course}
                  courseSubscription={courseSubscription}
                  currentUser={currentUser}
                  onlineCourses={assessmentOnlineCourses}
                  studySessionBundles={assessmentStudySessionBundles}
                  studySessions={assessmentStudySessions}
                  subscription={subscription}
                  userCourse={userCourse}
                />
              )
            : renderOnDemandContents()}
          </div>
          <div>
            {!!flashCardDecks?.length && (
              <StudentCoursePageFlashCardsDeckCard course={course} flashCardDecks={flashCardDecks} />
            )}
            <StudentCoursePageBookletCard classPassId={classPassId} course={course} />
            {hasGradeBoosters && renderOnDemandContents()}
          </div>
        </StudentCoursePageTwoColumnLayout>
      </StudentCoursePageContentsWrapper>
    </>
  );

  function renderOnDemandContents() {
    return (
      !!assessment?.contents && (
        <section>
          <StudentCoursePageSectionHeader title="Topics to Review" />
          <StudentCoursePageCard>
            <Typography>Check them off as you study them to prepare for your exam.</Typography>
            <div
              css={({ palette, spacing }) => ({
                listStyle: 'none',
                padding: 0,
                margin: spacing(1, 0, 0),
                '& > .item': {
                  display: 'flex',
                  alignItems: 'center',
                  padding: spacing(0.5, 0),
                  '& > .itemLink': {
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    '& > .itemPos': { flex: '0 0 24px', marginRight: spacing(2) },
                    '& > .itemTitle': { flex: 1 },
                  },
                  '& > .itemBtn': { padding: 2 },
                  '&.item_completed': {
                    '& .itemTitle': { textDecoration: 'line-through' },
                    '& .itemBtn': { color: palette.green.main },
                  },
                },
              })}
            >
              {renderContentMap(assessment.contents, (component, renderChildren) => {
                switch (component.type) {
                  case 'chapter': {
                    const completed = completedChapterIds.includes(component.data.id);
                    const firstActivity = findChild(component, n => n.type === 'activity');
                    return (
                      <div key={component.key} className={classNames('item', { item_completed: completed })}>
                        <ButtonBase
                          className="itemLink"
                          component={Link}
                          nativeButton={false}
                          to={`${basePath}${firstActivity?.fullPath || component.fullPath}`}
                        >
                          <Typography className="itemPos" sx={{ color: 'text.secondary' }} variant="h6">
                            {component.pos.padStart(2, '0')}
                          </Typography>
                          <Typography className="itemTitle" color="primary" variant="subtitle1">
                            {component.data.title}
                          </Typography>
                        </ButtonBase>
                        {userCourse && (
                          <IconButton
                            className="itemBtn"
                            onClick={() =>
                              updateCompletedChapters(
                                completed ?
                                  completedChapterIds.filter(id => id !== component.data.id)
                                : [...completedChapterIds, component.data.id],
                              )
                            }
                          >
                            <FontAwesomeIcon icon={completed ? faCircleCheckSolid : faCircleCheck} size="xs" />
                          </IconButton>
                        )}
                      </div>
                    );
                  }
                }
                return renderChildren();
              })}
            </div>
          </StudentCoursePageCard>
        </section>
      )
    );
  }
}
